import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, Check, ExternalLink, Plus, RefreshCw, User } from 'lucide-react';
import AssistantCompanyProfileResource, {
	PagePersonExtract,
	PageCompanyContact,
	PagePersonCompanyOption
} from 'Resources/AssistantCompanyProfile';
import { openCrmEntity } from 'App/shadcn/pages/Assistant/openCrmEntity';
import type { PageContext } from 'App/shadcn/pages/AIWorkspace/Chat/buildPageContextString';
import { parseLinkedInName } from './pageCompanyDomain';
import T from 'Components/Helpers/translate';

type Props = {
	pageContext: PageContext;
	// Per-URL extraction cache owned by EmbedAIChat so it survives tab switches.
	extractCache: Map<string, PagePersonExtract>;
	onPersonAdded: (name: string) => void;
};
type Phase = 'loading' | 'error' | 'ready';

// Send only the top of the page to the extractor: a LinkedIn profile's identity
// (name, headline, current role) sits near the start, so a smaller slice is enough
// and keeps the request — and the model's token load — light.
const EXTRACT_TEXT_LIMIT = 4000;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className="embed-add-fv">
		<span className="embed-add-fv-label">{label}</span>
		<span className="embed-add-fv-value">{children}</span>
	</div>
);

const ExternalLinkValue: React.FC<{ href: string; text: string }> = ({ href, text }) => (
	<a className="embed-add-fv-link" href={href} target="_blank" rel="noopener noreferrer">
		{text}
		<ExternalLink />
	</a>
);

const AddPersonTab: React.FC<Props> = ({ pageContext, extractCache, onPersonAdded }) => {
	const [phase, setPhase] = useState<Phase>('loading');
	const [extract, setExtract] = useState<PagePersonExtract | null>(null);
	const [step, setStep] = useState<1 | 2>(1);
	// The company search text — seeded from the extracted employer, editable in step 2.
	const [companyQuery, setCompanyQuery] = useState('');
	const [companyOptions, setCompanyOptions] = useState<PagePersonCompanyOption[]>([]);
	// null = "create a new company from the query text".
	const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
	const [searching, setSearching] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(false);
	const [added, setAdded] = useState(false);
	const [addedClient, setAddedClient] = useState<{ id: number; name: string } | null>(null);
	// Always the current URL, so a late response for a previous page is ignored.
	const latestUrl = useRef(pageContext.url);
	latestUrl.current = pageContext.url;
	// True once the user starts filling in the add form for this page, so a late server
	// enrichment (or a re-capture of the same page) can't reset what they're editing.
	const engagedRef = useRef(false);
	useEffect(() => {
		engagedRef.current = false;
	}, [pageContext.url]);

	const applyExtract = useCallback((result: PagePersonExtract) => {
		setExtract(result);
		setCompanyQuery(result.company?.name ?? result.person?.companyName ?? '');
		setSelectedClientId(result.company?.crm.exists ? result.company.crm.clientId ?? null : null);
		setCompanyOptions([]);
		setStep(1);
		setAdded(false);
		setAddedClient(null);
		setPhase('ready');
	}, []);

	// The name shown instantly, parsed from the page title — the server extract enriches it.
	const buildLocalExtract = useCallback((): PagePersonExtract | null => {
		const name = parseLinkedInName(pageContext.title);
		if (!name) return null;
		return { person: { name, linkedin: pageContext.url }, company: null };
	}, [pageContext.title, pageContext.url]);

	const runExtract = useCallback(() => {
		// Don't disturb the form once the user is filling it in for this page.
		if (engagedRef.current) return;
		const cached = extractCache.get(pageContext.url);
		if (cached) {
			applyExtract(cached);
			return;
		}
		// Show the title-derived name immediately so a profile never sits on a blank
		// "reading…" or a false "no person found" while the server extract is in flight.
		const seed = buildLocalExtract();
		if (seed) {
			applyExtract(seed);
		} else {
			setPhase('loading');
			setCompanyQuery('');
		}
		const reqUrl = pageContext.url;
		AssistantCompanyProfileResource.pagePersonExtract({
			url: pageContext.url,
			title: pageContext.title,
			text: pageContext.text.slice(0, EXTRACT_TEXT_LIMIT)
		})
			.then(result => {
				if (reqUrl !== latestUrl.current) return;
				if (result.person) {
					// Cache only a positive result — a null means the page hadn't rendered yet and
					// must be retried when a fuller capture arrives (else it sticks until reload).
					extractCache.set(reqUrl, result);
					if (!engagedRef.current) applyExtract(result);
				} else if (!seed) {
					setExtract(null);
					setPhase('ready');
				}
			})
			.catch(() => {
				if (reqUrl === latestUrl.current && !seed) setPhase('error');
			});
	}, [pageContext.url, pageContext.title, pageContext.text, extractCache, applyExtract, buildLocalExtract]);

	useEffect(() => {
		setSaveError(false);
		runExtract();
	}, [runExtract]);

	// Search the CRM for companies matching the query while on the company step.
	useEffect(() => {
		if (step !== 2) return;
		const q = companyQuery.trim();
		if (!q) {
			setCompanyOptions([]);
			return;
		}
		setSearching(true);
		const timer = setTimeout(() => {
			AssistantCompanyProfileResource.pagePersonCompanies(q)
				.then(list => {
					setCompanyOptions(list);
					setSearching(false);
				})
				.catch(() => {
					setCompanyOptions([]);
					setSearching(false);
				});
		}, 350);
		return () => clearTimeout(timer);
	}, [step, companyQuery]);

	const submit = () => {
		if (!extract?.person || saving || added) return;
		const person = extract.person;
		const company = extract.company;
		const trimmed = companyQuery.trim();
		if (selectedClientId == null && !trimmed) return;
		setSaving(true);
		setSaveError(false);
		// Creating a new company by name — reuse the extracted prospecting/domain only
		// when the name is still the one we resolved (the user didn't change it).
		const reuseExtract =
			selectedClientId == null && Boolean(company) && trimmed.toLowerCase() === company!.name.toLowerCase();
		const contact: PageCompanyContact = {
			name: person.name,
			role: person.title,
			company: trimmed || undefined,
			linkedin: person.linkedin
		};
		AssistantCompanyProfileResource.pageCompanyAdd({
			clientId: selectedClientId ?? undefined,
			prospectingId: reuseExtract ? company?.prospectingId : undefined,
			name: selectedClientId == null ? trimmed : undefined,
			domain: reuseExtract ? company?.domain : undefined,
			orgNumber: reuseExtract ? company?.orgNumber ?? undefined : undefined,
			contacts: [contact]
		})
			.then(result => {
				setSaving(false);
				setAdded(true);
				setAddedClient({ id: result.clientId, name: result.clientName });
				onPersonAdded(person.name);
			})
			.catch(() => {
				setSaving(false);
				setSaveError(true);
			});
	};

	const goToCompanyStep = () => {
		engagedRef.current = true;
		setStep(2);
	};

	if (phase === 'loading') {
		return <div className="embed-add embed-add-status">{T('assistant.embedPersonReading')}</div>;
	}
	if (phase === 'error') {
		return (
			<div className="embed-add embed-add-status">
				<div>{T('assistant.embedPersonError')}</div>
				<button type="button" className="embed-add-btn" onClick={runExtract}>
					<RefreshCw />
					{T('assistant.embedAddRetry')}
				</button>
			</div>
		);
	}
	if (!extract?.person) {
		return <div className="embed-add embed-add-status">{T('assistant.embedPersonNone')}</div>;
	}

	const person = extract.person;
	const existingContactId = person.existsInCrm ? person.contactId : undefined;
	const trimmedQuery = companyQuery.trim();
	const canSubmit = !saving && (selectedClientId != null || Boolean(trimmedQuery));
	const linkedinText = person.linkedin ? person.linkedin.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '') : '';

	if (added) {
		return (
			<div className="embed-add embed-add-status">
				<div className="embed-add-success-check">
					<Check />
				</div>
				<div className="embed-add-success-title">{T('assistant.embedPersonAdded')}</div>
				<div className="embed-add-success-sub">
					{addedClient ? `${person.name} · ${addedClient.name}` : person.name}
				</div>
				{addedClient ? (
					<button
						type="button"
						className="embed-add-btn embed-add-btn-primary"
						onClick={() => openCrmEntity('client', addedClient.id)}
					>
						<ExternalLink />
						{T('assistant.embedAddOpen')}
					</button>
				) : null}
			</div>
		);
	}

	return (
		<div className="embed-add">
			<div className="embed-add-scroll">
				{saveError ? <div className="embed-add-error">{T('assistant.embedAddSaveError')}</div> : null}

				{step === 1 ? (
					<>
						<div className="embed-add-item embed-add-item-static">
							<User className="embed-add-item-icon" />
							<span className="embed-add-item-info">
								<span className="embed-add-item-name">{person.name}</span>
								{person.title ? <span className="embed-add-item-meta">{person.title}</span> : null}
							</span>
							{existingContactId ? (
								<span className="embed-add-item-done" title={T('assistant.embedAddAlreadyContact')}>
									<Check />
								</span>
							) : null}
						</div>
						<div className="embed-add-fields">
							{person.title ? <Field label={T('default.title')}>{person.title}</Field> : null}
							{person.companyName ? (
								<Field label={T('default.client')}>{person.companyName}</Field>
							) : null}
							{person.linkedin ? (
								<Field label={T('default.linkedin')}>
									<ExternalLinkValue href={person.linkedin} text={linkedinText} />
								</Field>
							) : null}
						</div>
						{existingContactId ? (
							<>
								<button
									type="button"
									className="embed-add-btn embed-add-btn-primary"
									onClick={() => openCrmEntity('contact', existingContactId)}
								>
									<ExternalLink />
									{T('assistant.embedAddOpen')}
								</button>
								<button type="button" className="embed-add-back" onClick={() => setStep(2)}>
									{T('assistant.embedPersonAddAnyway')}
								</button>
							</>
						) : (
							<button
								type="button"
								className="embed-add-btn embed-add-btn-primary"
								onClick={goToCompanyStep}
							>
								{T('assistant.embedPersonNext')}
							</button>
						)}
					</>
				) : (
					<>
						<label className="embed-add-field">
							<span>{T('default.client')}</span>
							<input
								value={companyQuery}
								onChange={e => {
									engagedRef.current = true;
									setCompanyQuery(e.target.value);
									setSelectedClientId(null);
								}}
								placeholder={T('default.client')}
							/>
						</label>

						<div className="embed-add-company-list">
							{companyOptions.map(opt => {
								const meta = [opt.orgNumber, opt.domain].filter(Boolean).join(' · ');
								const isSelected = selectedClientId === opt.clientId;
								return (
									<button
										key={opt.clientId}
										type="button"
										className={`embed-add-item${isSelected ? ' is-selected' : ''}`}
										onClick={() => setSelectedClientId(opt.clientId)}
									>
										<Building2 className="embed-add-item-icon" />
										<span className="embed-add-item-info">
											<span className="embed-add-item-name">{opt.name}</span>
											{meta ? <span className="embed-add-item-meta">{meta}</span> : null}
										</span>
										{isSelected ? (
											<span className="embed-add-item-done">
												<Check />
											</span>
										) : null}
									</button>
								);
							})}

							{trimmedQuery ? (
								<button
									type="button"
									className={`embed-add-item${selectedClientId === null ? ' is-selected' : ''}`}
									onClick={() => setSelectedClientId(null)}
								>
									<Plus className="embed-add-item-icon" />
									<span className="embed-add-item-info">
										<span className="embed-add-item-name">{trimmedQuery}</span>
										<span className="embed-add-item-meta">
											{T('assistant.embedPersonWillCreateCompany')}
										</span>
									</span>
									{selectedClientId === null ? (
										<span className="embed-add-item-done">
											<Check />
										</span>
									) : null}
								</button>
							) : null}

							{searching ? <div className="embed-add-fv-label">{T('default.searching')}</div> : null}
						</div>

						<button
							type="button"
							className="embed-add-btn embed-add-btn-primary"
							disabled={!canSubmit}
							onClick={submit}
						>
							{T('default.addContact')}
						</button>
						<button type="button" className="embed-add-back" onClick={() => setStep(1)}>
							{T('assistant.embedPersonBack')}
						</button>
					</>
				)}
			</div>
		</div>
	);
};

export default AddPersonTab;
